import { useCallback, useEffect, useRef, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useWatch } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  HStack,
  IconButton,
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutContentLayout,
  StickyHeader,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import {
  FaAnglesLeftRegular,
  FaAnglesRightRegular,
} from '@enechain/ecloud-icons'

import { SimulationInputArea } from '~/pages/Simulation/components/OrderPatternInputArea'
import { SimulationAndResultArea } from '~/pages/Simulation/components/SimulationAndResultArea'
import { INITIAL_SIMULATION_FORM } from '~/pages/Simulation/constants/form'
import {
  OrderSimulationSchema,
  simulationSchema,
} from '~/pages/Simulation/schemas'
import { useGetMe } from '~/pages/hooks/useGetMe'
import { PATH_SIMULATION } from '~/routes/routePaths'

const RIGHT_PANEL_MIN_WIDTH = 450
const LEFT_PANEL_MIN_WIDTH = 400
const DIVIDER_WIDTH = 8
const INITIAL_RIGHT_PANEL_RATIO = 0.65 // サポート対象の最小の画面幅1280pxでスプレッドにしても横スクロールが発生しない幅に初期表示なるように調整
const INPUT_AREA_MIN_WIDTH_FOR_SPREAD = 750 // サポート対象の最小の画面幅1280pxで初期表示の割合で横スクロール発生しない かつ デザイン崩れしないように調整
const INPUT_AREA_MIN_WIDTH_FOR_NO_SPREAD = 400 // paddingやスクロールバーの幅も含めて右側エリアが最小幅のときに横スクロールが発生しない かつ デザイン崩れしないように調整

export const SimulationPage: React.FC = () => {
  const { t } = useTranslation(['page', 'domain'])
  const breadcrumbItems = [{ label: t(PATH_SIMULATION.name), bold: true }]
  const [showOrderInputField, setShowOrderInputField] = useState(true)
  const [containerWidth, setContainerWidth] = useState(0)
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    const estimatedWidth = window.innerWidth
    return Math.max(
      RIGHT_PANEL_MIN_WIDTH,
      Math.floor(estimatedWidth * INITIAL_RIGHT_PANEL_RATIO),
    )
  })
  const [isDragging, setIsDragging] = useState(false)
  const [isRightExpanded, setIsRightExpanded] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const formMethods = useForm<OrderSimulationSchema>({
    resolver: zodResolver(simulationSchema),
    mode: 'onChange',
    defaultValues: INITIAL_SIMULATION_FORM,
  })

  const orderPattern = useWatch({
    control: formMethods.control,
    name: 'orderPattern',
  })
  const hasSpread = orderPattern.some(
    (pattern) => pattern.spreadType.isSpreadTrade,
  )

  const { organizationName } = useGetMe()

  // コンテナの幅を監視
  useEffect(() => {
    const updateWidth = (): void => {
      if (containerRef.current != null) {
        const width = containerRef.current.offsetWidth
        setContainerWidth(width)
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(() => {
      updateWidth()
    })

    if (containerRef.current != null) {
      resizeObserver.observe(containerRef.current)
    }

    return (): void => {
      resizeObserver.disconnect()
    }
  }, [])

  // リサイズハンドラー
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDoubleClick = useCallback(() => {
    if (containerWidth > 0) {
      const newRightPanelWidth = isRightExpanded
        ? Math.floor(containerWidth * (1 - INITIAL_RIGHT_PANEL_RATIO)) -
          DIVIDER_WIDTH // 左パネルを0.65にする
        : Math.floor(containerWidth * INITIAL_RIGHT_PANEL_RATIO) // 右パネルを0.65にする

      setRightPanelWidth(newRightPanelWidth)
      setIsRightExpanded(!isRightExpanded)
    }
  }, [containerWidth, isRightExpanded])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || containerRef.current === null) {
        return
      }

      const containerRect = containerRef.current.getBoundingClientRect()
      const newRightPanelWidth = containerRect.right - e.clientX

      // 最小・最大幅の制約
      const maxRightWidth =
        containerWidth - LEFT_PANEL_MIN_WIDTH - DIVIDER_WIDTH
      const constrainedWidth = Math.max(
        RIGHT_PANEL_MIN_WIDTH,
        Math.min(maxRightWidth, newRightPanelWidth),
      )

      setRightPanelWidth(constrainedWidth)
    },
    [isDragging, containerWidth],
  )

  const handleMouseUp = useCallback((): void => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      return (): void => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    return undefined
  }, [isDragging, handleMouseMove, handleMouseUp])

  // 右パネルの幅を制約内に収める
  useEffect(() => {
    if (showOrderInputField && containerWidth > 0) {
      const maxRightWidth =
        containerWidth - LEFT_PANEL_MIN_WIDTH - DIVIDER_WIDTH
      if (rightPanelWidth > maxRightWidth) {
        setRightPanelWidth(maxRightWidth)
      }
    }
  }, [containerWidth, rightPanelWidth, showOrderInputField])

  const leftPanelWidth = showOrderInputField
    ? containerWidth - rightPanelWidth - DIVIDER_WIDTH
    : containerWidth

  return (
    <FormProvider {...formMethods}>
      <Flex
        ref={containerRef}
        alignItems="flex-start"
        width="100%"
        height="100vh"
      >
        <Box
          width={`${leftPanelWidth}px`}
          minWidth={
            showOrderInputField ? `${LEFT_PANEL_MIN_WIDTH}px` : undefined
          }
          height="100vh"
          transition={isDragging ? 'none' : 'width 0.3s ease-in-out'}
        >
          <SideMenuLayoutContentLayout
            header={
              <Flex justifyContent="space-between" width="full" height="full">
                <SideMenuLayoutBreadcrumb items={breadcrumbItems} />
                <HStack>
                  <Text textStyle="textMd" color="Text.highEmphasis">
                    {organizationName}
                  </Text>
                  {!showOrderInputField && (
                    <TertiaryButton
                      height="32px"
                      borderColor="Border.accentPrimary"
                      color="Text.accentPrimary"
                      onClick={() =>
                        setShowOrderInputField(!showOrderInputField)
                      }
                      leftIcon={
                        <FaAnglesLeftRegular color="Text.accentPrimary" />
                      }
                    >
                      {t('page:simulation.show_input_field_button')}
                    </TertiaryButton>
                  )}
                </HStack>
              </Flex>
            }
            padding={0}
          >
            <Box padding="4" overflowY="auto">
              <SimulationAndResultArea />
            </Box>
          </SideMenuLayoutContentLayout>
        </Box>
        {showOrderInputField && (
          <>
            <Tooltip
              label={t('page:simulation.divider_tooltip')}
              placement="left"
            >
              <Box
                width={`${DIVIDER_WIDTH}px`}
                height="100vh"
                background="Surface.secondary"
                cursor="col-resize"
                position="relative"
                onMouseDown={handleMouseDown}
                onDoubleClick={handleDoubleClick}
                _hover={{
                  background: 'Surface.tertiary',
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '2px',
                  height: '40px',
                  background: 'Border.highEmphasis',
                  borderRadius: '1px',
                }}
              />
            </Tooltip>
            <Box
              width={`${rightPanelWidth}px`}
              minWidth={`${RIGHT_PANEL_MIN_WIDTH}px`}
              height="100vh"
              transition={isDragging ? 'none' : 'width 0.3s ease-in-out'}
            >
              <StickyHeader
                header={
                  <Box
                    background="Surface.primary"
                    borderBottom="1px solid"
                    borderColor="Border.midEmphasis"
                    height="48px"
                    display="flex"
                    alignItems="center"
                    padding="4"
                    justifyContent="space-between"
                  >
                    <Text textStyle="headerMd">
                      {t('page:simulation.simulation_input_field')}
                    </Text>
                    <IconButton
                      aria-label="close"
                      onClick={() => setShowOrderInputField(false)}
                    >
                      <FaAnglesRightRegular />
                    </IconButton>
                  </Box>
                }
              >
                <Box overflowX="auto" overflowY="auto" padding="4" width="100%">
                  <Box
                    minWidth={
                      hasSpread
                        ? INPUT_AREA_MIN_WIDTH_FOR_SPREAD
                        : INPUT_AREA_MIN_WIDTH_FOR_NO_SPREAD
                    }
                  >
                    <SimulationInputArea />
                  </Box>
                </Box>
              </StickyHeader>
            </Box>
          </>
        )}
      </Flex>
    </FormProvider>
  )
}
